import { all, call, delay, fork, put, select, takeEvery } from 'redux-saga/effects';

import * as AppActions from '../actions/appAction';
import * as JobActions from '../actions/jobAction';
import * as LoginActions from '../actions/loginAction';
import * as UserActions from '../actions/userAction';
import { isElectron } from '../../utils/miscUtils';
import { API_BASE_URL_V2 } from '../../constants';
import { getAuthHeaders } from '../../api/shared';
import { IStore } from '../reducers';
import { JobStatus } from '../../models';
import { createGrainProfile, deleteGrainProfile, getAllGrains, getGrainProfiles, IGrainAPI, IGrainProfileAPI, isGrainProfileNotFoundError, updateGrainProfile } from '../../api/grain';
import { getUserSettings, updateUserSettings } from '../../api/user';
import {
  decideProfileSettingConflict,
  extractProfileSettingValue,
  isProfileSettingAuthError,
  isRejectSelectionSettingName,
  stableProfileSettingJson,
} from '../../utils/profileSettingSync';
import {
  isSyncIdentityReady,
  ISyncIdentity,
  selectPendingGrainProfilesForSync,
  selectProfileSettingsReadyForSync,
} from '../../utils/grainProfileState';

type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'pending_delete';
type LocalSyncCandidate = {
  jobId: string;
  jobStatus?: string;
  status?: string;
  processingMode?: string;
  syncStatus?: SyncStatus | string;
  syncAttempts?: number;
  lastSyncAttemptAt?: number;
  createdAt?: number;
  updatedAt?: number;
};
type LocalProfileSettingSyncCandidate = {
  username: string;
  orgId?: string;
  orgIdKey?: string;
  grainId: string;
  profileId: string;
  settingName: string;
  value?: any;
  valueJson?: string;
  lastSyncedValue?: any;
  lastSyncedValueJson?: string | null;
  syncStatus?: SyncStatus | string;
  syncAttempts?: number;
  lastSyncAttemptAt?: number;
  updatedAt?: number;
};
type LocalGrainProfileSyncCandidate = Partial<IGrainProfileAPI> & {
  grainId: string;
  profileId: string;
  syncStatus?: SyncStatus | string;
  syncError?: string | null;
};

const getNetworkStatus = (store: IStore) => store.app.isOnline;
const getUserLoggedIn = (store: IStore) => store.login.userLoggedIn;
const getUsername = (store: IStore) => store.user.userProfile.response?.username;
const getOrgId = (store: IStore) => store.user.userProfile.response?.org?.orgId;
const getCloudAuthStatus = (store: IStore) => store.app.cloudAuth?.status;

let syncInProgress = false;
let queuedForceSync = false;
let loadedSyncIdentity: ISyncIdentity | null = null;

// Throttle reference data sync to avoid excessive API calls
let lastReferenceDataSync = 0;
const REFERENCE_DATA_SYNC_MIN_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const STALE_LOCAL_PROCESSING_WINDOW_SECONDS = 30 * 60; // 30 minutes
const STALE_SYNCING_WINDOW_SECONDS = 10 * 60; // 10 minutes

function isLocalAlgoJobInProgress(status: unknown) {
  return status === JobStatus.JOB_PROCESSING_LOCAL || status === JobStatus.JOB_IN_PROGRESS;
}

function isLocalSyncableTerminalStatus(status: unknown) {
  return status === JobStatus.JOB_COMPLETED;
}

function getJobStatus(job: LocalSyncCandidate | undefined): unknown {
  return job?.jobStatus ?? job?.status;
}

function isStaleSyncingJob(job: LocalSyncCandidate, now: number) {
  if (job?.syncStatus !== 'syncing') {
    return false;
  }
  const lastAttemptAt = Number(job?.lastSyncAttemptAt || 0);
  if (!lastAttemptAt) {
    // Older records may not have attempt timestamps; allow retry.
    return true;
  }
  return now - lastAttemptAt >= STALE_SYNCING_WINDOW_SECONDS;
}

function canRunSync({
  isOnline,
  userLoggedIn,
  cloudAuthStatus
}: {
  isOnline: boolean;
  userLoggedIn: string;
  cloudAuthStatus?: string;
}) {
  // Local jobs can be created even when the user preference is "cloud" (offline fallback),
  // so allow sync whenever we're online on desktop and authenticated.
  return isOnline && isElectron() && userLoggedIn === 'LOGGED_IN' && cloudAuthStatus !== 'invalid';
}

function isCustomGrainProfile(profile: any): boolean {
  return profile?.profileType === 'CUSTOM' || `${profile?.profileId || ''}`.startsWith('custom_');
}

function filterPendingProfileSettings(
  settings: LocalProfileSettingSyncCandidate[],
  force: boolean,
  now: number
): LocalProfileSettingSyncCandidate[] {
  const eligible = (settings || []).filter((setting) => {
    if (!setting?.username || !setting?.grainId || !setting?.profileId || !setting?.settingName) {
      return false;
    }
    if (setting.syncStatus === 'syncing') {
      return force
        || Number(setting.lastSyncAttemptAt || 0) === 0
        || now - Number(setting.lastSyncAttemptAt || 0) >= STALE_SYNCING_WINDOW_SECONDS;
    }
    if (setting.syncStatus !== 'failed' || force) {
      return true;
    }
    const attempts = Number(setting.syncAttempts || 0);
    const lastAttemptAt = Number(setting.lastSyncAttemptAt || 0);
    if (!attempts || !lastAttemptAt) {
      return true;
    }
    const backoffSeconds = Math.min(60 * Math.pow(2, Math.max(0, attempts - 1)), 60 * 60);
    return now - lastAttemptAt >= backoffSeconds;
  });
  return force ? eligible : eligible.slice(0, 20);
}

function buildCloudGrainProfileCreatePayload(profile: LocalGrainProfileSyncCandidate): Partial<IGrainProfileAPI> {
  const legacyRejectSelectionConfig = (profile as any).rejectSelectionConfig;
  return {
    profileName: profile.profileName,
    brokenByShape: profile.brokenByShape,
    brokenThreshold: profile.brokenThreshold,
    particleDistributionRejectKeys: profile.particleDistributionRejectKeys
      || legacyRejectSelectionConfig?.particleDistribution?.selectedKeys,
    rejectedGrainsRejectKeys: profile.rejectedGrainsRejectKeys
      || legacyRejectSelectionConfig?.rejectedGrains?.selectedKeys,
    len_calib: profile.len_calib,
    width_calib: profile.width_calib,
    rice_type: profile.rice_type,
    add_calib_r: profile.add_calib_r,
    add_calib_g: profile.add_calib_g,
    add_calib_b: profile.add_calib_b,
    csv_profile: profile.csv_profile || '1',
    rejectionThresholds: profile.rejectionThresholds,
  };
}

function* updateGrainProfileSyncStatus(
  profile: LocalGrainProfileSyncCandidate,
  syncStatus: SyncStatus,
  syncError?: string | null,
  syncedProfile?: Partial<IGrainProfileAPI>
): Generator<any, void, any> {
  if (typeof window === 'undefined' || !(window as any).electron?.updateGrainProfile) {
    return;
  }
  yield call((window as any).electron.updateGrainProfile, {
    grainId: profile.grainId,
    profileId: profile.profileId,
    profileData: {
      ...profile,
      ...syncedProfile,
      syncStatus,
      syncError: syncError ?? null
    }
  });
}

function* syncGrainProfile(profile: LocalGrainProfileSyncCandidate): Generator<any, boolean, any> {
  if (profile.syncStatus === 'pending_delete') {
    try {
      yield call(deleteGrainProfile, profile.grainId, profile.profileId);
    } catch (error) {
      if (!isGrainProfileNotFoundError(error)) {
        throw error;
      }
    }
    const electron = typeof window !== 'undefined' ? (window as any).electron : null;
    if (electron?.deleteGrainProfile) {
      yield call(electron.deleteGrainProfile, {
        grainId: profile.grainId,
        profileId: profile.profileId,
        force: true
      });
    }
    return true;
  }

  yield call(updateGrainProfileSyncStatus as any, profile, 'syncing', null);
  const payload = buildCloudGrainProfileCreatePayload(profile);
  const isCustomProfile = isCustomGrainProfile(profile);
  const cloudProfile = isCustomProfile
    ? yield call(createGrainProfile, profile.grainId, payload)
    : yield call(updateGrainProfile, profile.grainId, profile.profileId, payload);

  const electron = typeof window !== 'undefined' ? (window as any).electron : null;
  if (isCustomProfile && electron?.reconcileSyncedCustomGrainProfile && cloudProfile?.profileId) {
    const localResult = yield call(electron.reconcileSyncedCustomGrainProfile, {
      grainId: cloudProfile.grainId || profile.grainId,
      oldProfileId: profile.profileId,
      profileData: {
        ...cloudProfile,
        syncStatus: 'synced',
        syncError: null
      }
    });
    if (localResult?.success === false) {
      throw new Error(localResult?.error || 'Failed to save synced profile locally');
    }
    return true;
  }

  yield call(updateGrainProfileSyncStatus as any, profile, 'synced', null, cloudProfile);
  return true;
}

function* updateLocalJobSyncStatus(
  jobId: string,
  syncStatus: SyncStatus,
  syncError?: string | null
): Generator<any, void, any> {
  if (typeof window === 'undefined' || !(window as any).electron?.updateLocalJobSyncStatus) {
    return;
  }
  yield call((window as any).electron.updateLocalJobSyncStatus, {
    jobId,
    syncStatus,
    syncError: syncError ?? null
  });
}

function parseMaybeJson(value: any): any {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function* regeneratePendingParticleDistributions(
  setting: LocalProfileSettingSyncCandidate
): Generator<any, void, any> {
  if (!`${setting.settingName || ''}`.startsWith('particle_distribution_') && !isRejectSelectionSettingName(setting.settingName)) {
    return;
  }
  if (
    typeof window === 'undefined' ||
    !(window as any).electron?.generateParticleDistributionCsvsForPendingJobs
  ) {
    return;
  }
  const resp = yield call((window as any).electron.generateParticleDistributionCsvsForPendingJobs, {
    ...profileSettingIdentity(setting)
  });
  if (!resp?.success) {
    const err: any = new Error(resp?.error || 'Failed to regenerate pending particle distribution CSVs');
    err.code = 'PARTICLE_REGEN_FAILED';
    throw err;
  }
}

function profileSettingIdentity(setting: LocalProfileSettingSyncCandidate) {
  return {
    username: setting.username,
    orgId: setting.orgId ?? setting.orgIdKey ?? '',
    grainId: setting.grainId,
    profileId: setting.profileId,
    settingName: setting.settingName
  };
}

function* updateProfileSettingSyncStatus(
  setting: LocalProfileSettingSyncCandidate,
  syncStatus: SyncStatus,
  syncError?: string | null,
  lastSyncedValue?: any
): Generator<any, void, any> {
  if (typeof window === 'undefined' || !(window as any).electron?.updateProfileSettingSyncStatus) {
    return;
  }
  yield call((window as any).electron.updateProfileSettingSyncStatus, {
    ...profileSettingIdentity(setting),
    syncStatus,
    syncError: syncError ?? null,
    lastSyncedValue
  });
}

function* upsertSyncedProfileSetting(
  setting: LocalProfileSettingSyncCandidate,
  value: any
): Generator<any, void, any> {
  if (typeof window === 'undefined' || !(window as any).electron?.upsertLocalProfileSetting) {
    return;
  }
  yield call((window as any).electron.upsertLocalProfileSetting, {
    ...profileSettingIdentity(setting),
    value,
    lastSyncedValue: value,
    syncStatus: 'synced'
  });
}

function* syncProfileSetting(setting: LocalProfileSettingSyncCandidate): Generator<any, boolean, any> {
  const value = setting.value !== undefined ? setting.value : parseMaybeJson(setting.valueJson);
  const lastSyncedValue =
    setting.lastSyncedValue !== undefined ? setting.lastSyncedValue : parseMaybeJson(setting.lastSyncedValueJson);

  yield call(updateProfileSettingSyncStatus as any, setting, 'syncing', null);

  const remoteResponse = yield call(getUserSettings, profileSettingIdentity(setting));
  const remoteValue = extractProfileSettingValue({
    response: remoteResponse,
    settingName: setting.settingName,
    grainId: setting.grainId,
    profileId: setting.profileId
  });
  const preferLocalParticleSettingDuringDesktopSync =
    isElectron() &&
    (`${setting.settingName || ''}`.startsWith('particle_distribution_') || isRejectSelectionSettingName(setting.settingName)) &&
    lastSyncedValue === undefined;

  if (preferLocalParticleSettingDuringDesktopSync) {
    yield call(updateUserSettings, {
      username: setting.username,
      grainId: setting.grainId,
      profileId: setting.profileId,
      settingName: setting.settingName,
      newSetting: value
    });
    yield call(updateProfileSettingSyncStatus as any, setting, 'synced', null, value);
    yield call(regeneratePendingParticleDistributions as any, setting);
    return true;
  }

  const conflictDecision = decideProfileSettingConflict({
    remoteValue,
    remoteSource: remoteResponse?.source,
    lastSyncedValue,
    settingName: setting.settingName,
  });

  if (conflictDecision === 'cloudWins') {
    yield call(upsertSyncedProfileSetting as any, setting, remoteValue);
    yield call(regeneratePendingParticleDistributions as any, setting);
    yield put(UserActions.setUserSettingsValue({
      key: `${setting.orgId ?? setting.orgIdKey ?? ''}:${setting.grainId || ''}:${setting.profileId || ''}:${setting.settingName || ''}`,
      value: remoteValue
    }));
    return true;
  }

  yield call(updateUserSettings, {
    username: setting.username,
    grainId: setting.grainId,
    profileId: setting.profileId,
    settingName: setting.settingName,
    newSetting: value
  });
  yield call(updateProfileSettingSyncStatus as any, setting, 'synced', null, value);
  if (stableProfileSettingJson(value) !== stableProfileSettingJson(lastSyncedValue)) {
    yield call(regeneratePendingParticleDistributions as any, setting);
  }
  return true;
}

function* markCloudAuthInvalid(reason: 'not_authorized' | 'api_401'): Generator<any, void, any> {
  yield put(
    AppActions.setCloudAuthStatus({
      status: 'invalid',
      reason,
      updatedAt: Date.now()
    })
  );
}

function* syncJob(job: any, apiBaseUrl: string, token: string, force: boolean): Generator<any, void, any> {
  if (typeof window === 'undefined' || !(window as any).electron?.syncLocalJobToCloud) {
    throw new Error('Electron sync IPC is not available');
  }

  const resp = yield call((window as any).electron.syncLocalJobToCloud, {
    jobId: job.jobId,
    apiBaseUrl,
    token,
    // Manual sync should attempt upload even if host-level reachability checks are noisy.
    skipReachabilityCheck: force === true,
    currentSyncStatus: job.syncStatus
  });

  if (!resp?.success) {
    if (resp?.offline === true || resp?.errorCode === 'OFFLINE') {
      const err: any = new Error(resp?.error || 'Offline');
      err.code = 'OFFLINE';
      throw err;
    }
    const err: any = new Error(resp?.error || 'Sync failed');
    if (resp?.errorCode) {
      err.code = resp.errorCode;
    }
    throw err;
  }
}

/**
 * Sync grain reference data (grains + profiles) from the API into the local Electron DB.
 * This ensures that profiles created or updated in the cloud are available for offline job processing.
 * Throttled to avoid excessive API calls.
 */
function* syncReferenceData(): Generator<any, void, any> {
  if (!isElectron() || typeof window === 'undefined') return;
  const electron = (window as any).electron;
  if (!electron?.upsertReferenceData) return;

  const isOnline: boolean = yield select(getNetworkStatus);
  const userLoggedIn: string = yield select(getUserLoggedIn);
  const username: string | undefined = yield select(getUsername);
  const orgId: string | undefined = yield select(getOrgId);
  const cloudAuthStatus: string | undefined = yield select(getCloudAuthStatus);
  if (!canRunSync({ isOnline, userLoggedIn, cloudAuthStatus })) return;
  if (!isSyncIdentityReady(loadedSyncIdentity, { username, orgId })) return;

  const now = Date.now();
  if (now - lastReferenceDataSync < REFERENCE_DATA_SYNC_MIN_INTERVAL_MS) return;
  lastReferenceDataSync = now;

  try {
    const grains: IGrainAPI[] = yield call(getAllGrains);
    const profileArrays: IGrainProfileAPI[][] = yield all(grains.map((g) => call(getGrainProfiles, g.grainId)));
    const profiles = profileArrays.flat();
    yield call(electron.upsertReferenceData, { grains, profiles });
  } catch (error: any) {
    console.warn('Failed to sync grain reference data from API to local database:', error?.message || error);
  }
}

function* syncOnce(force = false): Generator<any, void, any> {
  if (syncInProgress) {
    if (force) {
      queuedForceSync = true;
    }
    return;
  }
  syncInProgress = true;
  let shouldRunQueuedForceSync = false;
  const isOnline: boolean = yield select(getNetworkStatus);
  const userLoggedIn: string = yield select(getUserLoggedIn);
  const username: string | undefined = yield select(getUsername);
  const orgId: string | undefined = yield select(getOrgId);
  const cloudAuthStatus: string | undefined = yield select(getCloudAuthStatus);

  try {
    if (!canRunSync({ isOnline, userLoggedIn, cloudAuthStatus })) {
      return;
    }
    if (!isSyncIdentityReady(loadedSyncIdentity, { username, orgId })) {
      return;
    }

    if (typeof window === 'undefined' || !(window as any).electron?.getLocalJobs) {
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const localJobs: LocalSyncCandidate[] = yield call((window as any).electron.getLocalJobs, { username });
    const hasLocalAlgoInProgress = !force && (localJobs || []).some((job) => {
      if (!job || job.processingMode !== 'local') {
        return false;
      }
      const status = getJobStatus(job);
      if (!isLocalAlgoJobInProgress(status)) {
        return false;
      }
      const updatedAt = Number(job?.updatedAt || 0);
      if (!updatedAt) {
        return true;
      }
      return now - updatedAt < STALE_LOCAL_PROCESSING_WINDOW_SECONDS;
    });
    if (hasLocalAlgoInProgress) {
      // Algo processing is more critical than background sync; avoid contention (disk/CPU/Python).
      return;
    }
    const pending = (localJobs || [])
      .filter((job) => job?.processingMode === 'local')
      .filter((job) => isLocalSyncableTerminalStatus(getJobStatus(job)))
      .filter((job) => job?.syncStatus !== 'synced')
      .filter((job) => {
        if (job?.syncStatus === 'syncing') {
          return force || isStaleSyncingJob(job, now);
        }
        if (job?.syncStatus !== 'failed') {
          return true;
        }
        if (force) {
          return true;
        }
        const attempts = Number(job?.syncAttempts || 0);
        const lastAttemptAt = Number(job?.lastSyncAttemptAt || 0);
        if (!attempts || !lastAttemptAt) {
          return true;
        }
        const backoffSeconds = Math.min(60 * Math.pow(2, Math.max(0, attempts - 1)), 60 * 60);
        return now - lastAttemptAt >= backoffSeconds;
      })
      .sort((a, b) => {
        if (force) {
          // Manual sync should prioritize the most recent jobs first.
          return (b.createdAt || 0) - (a.createdAt || 0);
        }
        return (a.createdAt || 0) - (b.createdAt || 0);
      });

    // Manual sync should process all eligible unsynced jobs.
    // Background sync stays bounded per cycle.
    const batch = force ? pending : pending.slice(0, 5);

    let pendingProfileSettings: LocalProfileSettingSyncCandidate[] = [];
    if (typeof window !== 'undefined' && (window as any).electron?.getPendingProfileSettings) {
      const settingsResp = yield call((window as any).electron.getPendingProfileSettings, { username });
      pendingProfileSettings = filterPendingProfileSettings(settingsResp?.settings || [], force, now);
    }

    let localGrainProfiles: LocalGrainProfileSyncCandidate[] = [];
    let pendingGrainProfiles: LocalGrainProfileSyncCandidate[] = [];
    if (typeof window !== 'undefined' && (window as any).electron?.getGrainProfiles) {
      const profilesResp = yield call((window as any).electron.getGrainProfiles, { includePendingDelete: true });
      localGrainProfiles = Array.isArray(profilesResp) ? profilesResp : [];
      pendingProfileSettings = selectProfileSettingsReadyForSync(pendingProfileSettings, localGrainProfiles);
      pendingGrainProfiles = selectPendingGrainProfilesForSync(localGrainProfiles, { username, orgId });
      if (!force) {
        pendingGrainProfiles = pendingGrainProfiles.filter((profile) => profile.syncStatus !== 'failed').slice(0, 10);
      }
    }

    if (batch.length === 0 && pendingProfileSettings.length === 0 && pendingGrainProfiles.length === 0) {
      return;
    }

    const apiBaseUrl = API_BASE_URL_V2();
    let token: string | undefined;
    try {
      const authHeaders: Record<string, string> = yield call(getAuthHeaders, true);
      const authHeader = authHeaders?.Authorization || (authHeaders as any)?.authorization;
      token = typeof authHeader === 'string' && authHeader.startsWith('Bearer ') ? authHeader.slice(7) : undefined;
    } catch {
      // Auth not available (expired session, etc). Pause sync until the user re-authenticates.
      yield call(markCloudAuthInvalid, 'not_authorized');
      return;
    }
    if (!token) {
      yield call(markCloudAuthInvalid, 'not_authorized');
      return;
    }

    // Best-effort reachability check to avoid spawning Python when offline/unreachable.
    if (!force && typeof window !== 'undefined' && (window as any).electron?.checkApiReachable) {
      const reachResp = yield call((window as any).electron.checkApiReachable, {
        apiBaseUrl,
        timeoutMs: 2000
      });
      if (!reachResp?.success || reachResp?.reachable !== true) {
        return;
      }
    }

    let syncedCount = 0;
    for (const profile of pendingGrainProfiles) {
      try {
        yield call(syncGrainProfile as any, profile);
      } catch (error: any) {
        if (isProfileSettingAuthError(error)) {
          yield call(markCloudAuthInvalid, 'api_401');
          yield call(
            updateGrainProfileSyncStatus as any,
            profile,
            profile.syncStatus === 'pending_delete' ? 'pending_delete' : 'pending',
            'Sign-in expired (sync paused)'
          );
          return;
        }
        yield call(
          updateGrainProfileSyncStatus as any,
          profile,
          profile.syncStatus === 'pending_delete' ? 'pending_delete' : 'failed',
          error?.message || 'Profile sync failed'
        );
      }
    }

    if (pendingGrainProfiles.length > 0 && typeof window !== 'undefined' && (window as any).electron?.getPendingProfileSettings) {
      const settingsResp = yield call((window as any).electron.getPendingProfileSettings, { username });
      pendingProfileSettings = filterPendingProfileSettings(settingsResp?.settings || [], force, now);
      if ((window as any).electron?.getGrainProfiles) {
        const profilesResp = yield call((window as any).electron.getGrainProfiles, { includePendingDelete: true });
        localGrainProfiles = Array.isArray(profilesResp) ? profilesResp : [];
        pendingProfileSettings = selectProfileSettingsReadyForSync(
          pendingProfileSettings,
          localGrainProfiles
        );
      }
    }

    for (const setting of pendingProfileSettings) {
      try {
        yield call(syncProfileSetting as any, setting);
      } catch (error: any) {
        if (isProfileSettingAuthError(error)) {
          yield call(markCloudAuthInvalid, 'api_401');
          yield call(updateProfileSettingSyncStatus as any, setting, 'pending', 'Sign-in expired (sync paused)');
          return;
        }
        yield call(updateProfileSettingSyncStatus as any, setting, 'failed', error?.message || 'Settings sync failed');
        if (error?.code === 'PARTICLE_REGEN_FAILED') {
          return;
        }
      }
    }

    for (const job of batch) {
      try {
        yield call(updateLocalJobSyncStatus as any, job.jobId, 'syncing', null);
        yield call(syncJob as any, job, apiBaseUrl, token, force);
        yield call(updateLocalJobSyncStatus as any, job.jobId, 'synced', null);
        syncedCount++;
      } catch (error: any) {
        if (error?.code === 'OFFLINE') {
          yield call(updateLocalJobSyncStatus as any, job.jobId, 'pending', null);
        } else if (error?.code === 'AUTH_EXPIRED') {
          yield call(markCloudAuthInvalid, 'api_401');
          yield call(updateLocalJobSyncStatus as any, job.jobId, 'pending', 'Sign-in expired (sync paused)');
          return;
        } else {
          yield call(updateLocalJobSyncStatus as any, job.jobId, 'failed', error?.message || 'Sync failed');
        }
      }
    }

    // Trigger immediate refetch of user jobs if any jobs were synced
    if (syncedCount > 0 && username) {
      yield put(JobActions.userJobs.request({ username }));
    }
  } finally {
    syncInProgress = false;
    shouldRunQueuedForceSync = queuedForceSync;
    queuedForceSync = false;
  }

  if (shouldRunQueuedForceSync) {
    yield call(syncOnce, true);
  }
}

function* onNetworkStatusChanged(action: ReturnType<typeof AppActions.setNetworkStatus>): Generator<any, void, any> {
  if (action.payload === true) {
    if (!loadedSyncIdentity) {
      const userLoggedIn: string = yield select(getUserLoggedIn);
      if (userLoggedIn === 'LOGGED_IN') {
        yield put(UserActions.getUserProfile.request());
      }
      return;
    }
    yield call(syncOnce, false);
    yield call(syncReferenceData);
  }
}

function* onLoginStatusChanged(_action: ReturnType<typeof LoginActions.setUserLoggedIn>): Generator<any, void, any> {
  loadedSyncIdentity = null;
  lastReferenceDataSync = 0;
}

function* onUserProfileLoaded(action: ReturnType<typeof UserActions.getUserProfile.success>): Generator<any, void, any> {
  if (!action.payload?.profile?.username) {
    return;
  }

  const nextIdentity = {
    username: action.payload.profile.username,
    orgId: action.payload.profile.org?.orgId,
  };
  const userLoggedIn: string = yield select(getUserLoggedIn);
  const username: string | undefined = yield select(getUsername);
  const orgId: string | undefined = yield select(getOrgId);
  if (userLoggedIn !== 'LOGGED_IN' || !isSyncIdentityReady(nextIdentity, { username, orgId })) {
    return;
  }

  loadedSyncIdentity = nextIdentity;
  lastReferenceDataSync = 0;

  const electron = typeof window !== 'undefined' ? (window as any).electron : null;
  if (electron?.discardOwnerlessPendingProfiles) {
    const discardResult = yield call(electron.discardOwnerlessPendingProfiles);
    if (discardResult?.success === false) {
      console.warn('Failed to discard ownerless pending profiles:', discardResult?.error);
    } else if (discardResult?.discardedProfiles > 0 || discardResult?.discardedSettings > 0) {
      console.info('Discarded legacy ownerless pending profile data:', {
        profiles: discardResult.discardedProfiles,
        settings: discardResult.discardedSettings,
      });
    }
  }

  yield call(syncOnce, true);
  yield call(syncReferenceData);
}

function* syncLoop(): Generator<any, void, any> {
  // Low-frequency poll to catch cases where we became online while app was asleep.
  while (true) {
    yield delay(60 * 1000);
    yield call(syncOnce, false);
  }
}

export function* watchSyncNow(): Generator<any, void, any> {
  yield takeEvery(AppActions.syncLocalJobsNow, function* syncNowWorker() {
    yield call(syncOnce, true);
  });
}

export function* watchNetworkStatusForSync(): Generator<any, void, any> {
  yield takeEvery(AppActions.setNetworkStatus, onNetworkStatusChanged);
}

export function* watchLoginForReferenceSync(): Generator<any, void, any> {
  yield takeEvery(LoginActions.setUserLoggedIn, onLoginStatusChanged);
}

export function* watchUserProfileForSync(): Generator<any, void, any> {
  yield takeEvery(UserActions.getUserProfile.success, onUserProfileLoaded);
}

export default function* root(): Generator<any, void, any> {
  yield all([
    fork(watchNetworkStatusForSync),
    fork(watchLoginForReferenceSync),
    fork(watchUserProfileForSync),
    fork(watchSyncNow),
    fork(syncLoop)
  ]);
}
